import securedAxios from '~/javascript/utils/axios'

const axios = securedAxios()

const CACHE_KEY = 'health_sync_summary_cache'
const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes

// Cache helpers (defined first to avoid use-before-define)
function cacheResponse(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      data,
      timestamp: new Date().toISOString()
    }))
  } catch {
    // Ignore localStorage errors
  }
}

function getCachedResponse() {
  try {
    const cached = localStorage.getItem(CACHE_KEY)
    if (!cached) return null

    const parsed = JSON.parse(cached)
    const cachedTime = new Date(parsed.timestamp).getTime()
    const now = Date.now()

    // Return cached data if within TTL
    if (now - cachedTime < CACHE_TTL_MS) {
      return parsed
    }

    // Cache expired
    localStorage.removeItem(CACHE_KEY)
    return null
  } catch {
    return null
  }
}

/**
 * Get health sync summary from API with localStorage fallback
 * @returns {Promise<Object>} Sync summary data
 */
export async function getHealthSyncSummary() {
  try {
    const response = await axios.get('/api/v1/sync_status')
    const data = response.data

    // Cache successful response
    cacheResponse(data)

    return { data, cached: false }
  } catch (error) {
    // On error, try to return cached data
    const cached = getCachedResponse()
    if (cached) {
      return { data: cached.data, cached: true, cachedAt: cached.timestamp }
    }

    throw error
  }
}

/**
 * Request a sync from the iOS device
 * @param {string} provider - Provider name (default: 'healthkit')
 * @returns {Promise<Object>} Result with success status and request data
 */
export async function requestSync(provider = 'healthkit') {
  try {
    const response = await axios.post('/api/v1/sync_requests', { provider })

    return {
      success: true,
      requestId: response.data.id,
      expiresAt: response.data.expires_at,
      message: response.data.message
    }
  } catch (error) {
    const status = error.response?.status
    const data = error.response?.data || {}

    if (status === 422) {
      // No device token
      return {
        success: false,
        error: 'no_device_token',
        message: data.message || 'Please enable push notifications in the iOS app'
      }
    }

    if (status === 409) {
      // Request pending
      return {
        success: false,
        error: 'request_pending',
        message: data.message || 'A sync request is already pending',
        pendingRequest: data.pending_request
      }
    }

    if (status === 429) {
      // Rate limited
      return {
        success: false,
        error: 'rate_limited',
        message: data.message || 'Too many requests. Please try again later.',
        retryAfter: data.retry_after
      }
    }

    // Unknown error
    return {
      success: false,
      error: 'unknown',
      message: data.message || 'Failed to request sync'
    }
  }
}

/**
 * Get sync request status by ID
 * @param {string} requestId - Sync request ID
 * @returns {Promise<Object>} Request status data
 */
export async function getSyncRequestStatus(requestId) {
  const response = await axios.get(`/api/v1/sync_requests/${requestId}`)
  return response.data
}

/**
 * Clear the sync summary cache
 */
export function clearSyncCache() {
  try {
    localStorage.removeItem(CACHE_KEY)
  } catch {
    // Ignore localStorage errors
  }
}
